import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import { CartProvider } from './context/CartContext';
import StorePage from './pages/StorePage';
import BlogPage from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import ArticlePage from './pages/ArticlePage';
import TrialPage from './pages/TrialPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import SamplesPage from './pages/SamplesPage';
import DownloadHelpPage from './pages/DownloadHelpPage';
import PurchaseNotification from './components/PurchaseNotification';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// HomePage Component - Contiene todo el contenido original
function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const [visibleMusicCards, setVisibleMusicCards] = useState([]);
  const cardsRef = useRef([]);
  const musicCardsRef = useRef([]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = entry.target.dataset.index;
            setVisibleCards((prev) => [...prev, index]);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  // Intersection Observer for music/culture cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = entry.target.dataset.index;
            setVisibleMusicCards((prev) => [...prev, index]);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    musicCardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      musicCardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img src="/logo.webp" alt="Vencejo Academy Logo" className="h-12 w-auto mr-3" />
                <span className="text-3xl font-display font-bold text-primary-600">Vencejo Academy</span>
              </Link>
            </div>
            <div className="hidden md:flex space-x-8 items-center">
              <Link to="/store" className="text-gray-700 hover:text-primary-600 transition font-medium">STORE</Link>
              <Link to="/blog" className="text-gray-700 hover:text-primary-600 transition font-medium">BLOG</Link>
              <Link to="/contact" className="text-gray-700 hover:text-primary-600 transition font-medium">CONTACT</Link>
              <Link to="/tutoring-hub" className="block text-gray-700 hover:text-primary-600 transition">TUTORING HUB</Link>
              <Link to="/free-ebook" className="font-medium text-red-600 hover:text-red-700 transition">FREE EBOOK</Link>
            </div>
            <div className="md:hidden">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 focus:outline-none"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
          {isMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <Link to="/store" className="block text-gray-700 hover:text-primary-600 transition">STORE</Link>
              <Link to="/blog" className="block text-gray-700 hover:text-primary-600 transition">BLOG</Link>
              <Link to="/contact" className="block text-gray-700 hover:text-primary-600 transition">CONTACT</Link>
              <Link to="/tutoring-hub" className="block text-gray-700 hover:text-primary-600 transition">TUTORING HUB</Link>
              <Link to="/free-ebook" className="block font-medium text-red-600 hover:text-red-700 transition text-center">FREE EBOOK</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="relative py-24 lg:py-32 overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/images/spanish.learn.webp)',
          backgroundColor: '#FEF9C3'
        }}
      >
        {/* Background Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-100/95 via-secondary-50/92 to-primary-50/95"></div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary-300 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-300 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-display font-bold text-gray-900 mb-6 leading-tight">
            ¿Aprendes español y<br />
            <span className="text-primary-600">te gusta la cultura?</span>
          </h1>
          <h2 className="text-2xl md:text-4xl font-display font-semibold text-gray-700 mb-8">
            Learn with Music and Cultural Experiences!
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Immerse yourself in the Spanish language through authentic music, movies, personalized tutoring, quizzes, and rich cultural insights including recipes. 
            Your journey to fluency starts here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/store" className="bg-primary-600 text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-primary-700 transition transform hover:scale-105 shadow-lg text-center">
              EXPLORE PRODUCTS
            </Link>
            <Link to="/free-ebook" className="bg-white text-red-600 border-2 border-red-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-red-50 transition transform hover:scale-105 text-center">
              GET FREE EBOOK
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section - Spanish Novels Style */}
      <section className="py-20" style={{ backgroundColor: '#FF6B6B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              Complete Spanish Learning Experience
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Interactive course packs with tutor support and face-to-face options
            </p>
          </div>

          {/* Statistics Section */}
          <div className="flex justify-center items-center space-x-12 mb-16 flex-wrap gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-display font-bold text-white mb-2">+150,000</div>
              <div className="text-white/90 font-medium">Books Sold</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-display font-bold text-white mb-2">+1,200</div>
              <div className="text-white/90 font-medium">Bundle Packs Sold</div>
            </div>
          </div>

          {/* Main Heading */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              Learn Spanish with Immersive Course Packs!
            </h2>
            <p className="text-lg text-white/90 max-w-3xl mx-auto">
              <strong>Interactive Learning</strong> with Tutor Support and Face-to-Face Options from Beginner (A1) to Advanced (C2)
            </p>
          </div>

          {/* Features Grid - Spanish Novels Style with Dark Blue Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* DIGITAL COURSES */}
            <div 
              ref={(el) => (cardsRef.current[0] = el)}
              data-index="0"
              className={`icon-card bg-blue-900 p-6 rounded-lg text-center cursor-pointer transition-all duration-500 hover:shadow-2xl ${visibleCards.includes('0') ? 'visible' : ''}`}
              style={{ backgroundColor: '#1e3a8a' }}
            >
              <div className="flex justify-center mb-4">
                <div className="icon-circle relative w-20 h-20 rounded-full bg-black border-2 border-white flex items-center justify-center">
                  <svg className="icon-svg w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.832 5.477 3.75 6.253v13C4.832 18.477 6.246 19 7.5 19s2.668-.477 3.75-1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.25 1.253v13C19.832 18.477 18.247 19 16.5 19c-1.746 0-3.332-.477-4.25-1.253" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-display font-bold text-white mb-3">DIGITAL COURSES</h3>
              <p className="text-white text-sm leading-relaxed text-left">
                Complete course packs with ebooks, personalized tutoring sessions, interactive quizzes, and exercises. Access all materials on any device - computer, smartphone, or tablet. Download and learn at your own pace!
              </p>
            </div>

            {/* AUDIO LESSONS */}
            <div 
              ref={(el) => (cardsRef.current[1] = el)}
              data-index="1"
              className={`icon-card bg-blue-900 p-6 rounded-lg text-center cursor-pointer transition-all duration-500 hover:shadow-2xl ${visibleCards.includes('1') ? 'visible' : ''}`}
              style={{ backgroundColor: '#1e3a8a' }}
            >
              <div className="flex justify-center mb-4">
                <div className="icon-circle relative w-20 h-20 rounded-full bg-black border-2 border-white flex items-center justify-center">
                  <svg className="icon-svg w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-display font-bold text-white mb-3">AUDIO LESSONS</h3>
              <p className="text-white text-sm leading-relaxed text-left">
                High-quality audio lessons with song translations, pronunciation guides, and listening exercises. Learn with music, stories, and cultural content. Available in multiple Spanish dialects.
              </p>
            </div>

            {/* TUTOR SUPPORT */}
            <div 
              ref={(el) => (cardsRef.current[2] = el)}
              data-index="2"
              className={`icon-card bg-blue-900 p-6 rounded-lg text-center cursor-pointer transition-all duration-500 hover:shadow-2xl ${visibleCards.includes('2') ? 'visible' : ''}`}
              style={{ backgroundColor: '#1e3a8a' }}
            >
              <div className="flex justify-center mb-4">
                <div className="icon-circle relative w-20 h-20 rounded-full bg-black border-2 border-white flex items-center justify-center">
                  <svg className="icon-svg w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-display font-bold text-white mb-3">TUTOR SUPPORT</h3>
              <p className="text-white text-sm leading-relaxed text-left">
                Get personalized help from an experienced Spanish tutor. Receive guidance, feedback on your progress, and answers to your questions throughout your learning journey.
              </p>
            </div>

            {/* FACE-TO-FACE COURSES */}
            <div 
              ref={(el) => (cardsRef.current[3] = el)}
              data-index="3"
              className={`icon-card bg-blue-900 p-6 rounded-lg text-center cursor-pointer transition-all duration-500 hover:shadow-2xl ${visibleCards.includes('3') ? 'visible' : ''}`}
              style={{ backgroundColor: '#1e3a8a' }}
            >
              <div className="flex justify-center mb-4">
                <div className="icon-circle relative w-20 h-20 rounded-full bg-black border-2 border-white flex items-center justify-center">
                  <svg className="icon-svg w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-display font-bold text-white mb-3">FACE-TO-FACE COURSES</h3>
              <p className="text-white text-sm leading-relaxed text-left">
                Optional in-person classes available! Practice Spanish in real conversations, get immediate feedback, and learn in a supportive group environment. Perfect for immersive learning!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Music & Cultural Insights Section - Combined */}
      <section id="music" className="py-20" style={{ backgroundColor: '#FF6B6B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Heading */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4" style={{ fontFamily: 'serif' }}>
              Learn Spanish Through Music and Culture!
            </h2>
            <p className="text-lg text-white/95 max-w-3xl mx-auto" style={{ fontFamily: 'sans-serif' }}>
              Comprehensive Learning combining music, videos, podcasts, and cultural insights
            </p>
          </div>

          {/* Features Grid - White Cards on Coral Background */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* MUSIC COLLECTIONS */}
            <div 
              ref={(el) => (musicCardsRef.current[0] = el)}
              data-index="0"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('0') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>MUSIC COLLECTIONS</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Learn Spanish through song translations with complete lyrics and grammar explanations. Our collections include 40+ songs in the Basic pack, up to 100 songs in the Complete program, covering diverse genres from traditional folk to modern pop to enhance your language learning journey.
              </p>
            </div>

            {/* ARTIST SPOTLIGHTS */}
            <div 
              ref={(el) => (musicCardsRef.current[1] = el)}
              data-index="1"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('1') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>FILM RECOMMENDATIONS</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Discover 25 curated film recommendations with detailed study guides. Learn Spanish through authentic dialogues, cultural context, and vocabulary exercises. Perfect for improving listening comprehension and understanding real-world Spanish conversations.
              </p>
            </div>

            {/* VIDEO CONTENT */}
            <div 
              ref={(el) => (musicCardsRef.current[2] = el)}
              data-index="2"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('2') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>PERSONAL TUTORING</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Join our Tutoring Hub for personalized 1-on-1 sessions. From individual 30-minute sessions to complete fluency bundles. Choose the pack that fits your needs and start speaking Spanish confidently with our expert tutors.
              </p>
            </div>

            {/* PODCASTS */}
            <div 
              ref={(el) => (musicCardsRef.current[3] = el)}
              data-index="3"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('3') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>INTERACTIVE QUIZZES</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Test your knowledge with comprehensive interactive quizzes and exercises. From 25+ quizzes in Basic to comprehensive quizzes in all packs, practice grammar, vocabulary, and comprehension. Track your progress and reinforce what you've learned.
              </p>
            </div>

            {/* ARTICLES & BLOGS */}
            <div 
              ref={(el) => (musicCardsRef.current[4] = el)}
              data-index="4"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('4') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>GRADED EBOOKS & AUDIOBOOKS</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Access graded ebooks from beginner (A1) to advanced (C1) levels with accompanying audiobooks. Available in Interactive Platform and Complete Program. Read and listen to stories designed to challenge you gradually and build your Spanish skills naturally.
              </p>
            </div>

            {/* FOOD & CUISINE */}
            <div 
              ref={(el) => (musicCardsRef.current[5] = el)}
              data-index="5"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('5') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>RECIPE EBOOKS</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Explore authentic recipe ebooks from Spanish-speaking countries. Available in Interactive Platform with authentic recipes, and complete library access in Complete Program. Learn culinary vocabulary, cultural food traditions, and discover the flavors of the Spanish-speaking world.
              </p>
            </div>

            {/* TRADITIONS & FESTIVALS */}
            <div 
              ref={(el) => (musicCardsRef.current[6] = el)}
              data-index="6"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('6') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>CULTURAL CONTEXT</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Learn authentic dialogues and cultural context organized by country. Perfect for beginners planning a trip. Understand traditions, festivals, and everyday conversations. Immerse yourself in the rich cultural heritage of Spanish-speaking countries.
              </p>
            </div>

            {/* TRAVEL GUIDES */}
            <div 
              ref={(el) => (musicCardsRef.current[7] = el)}
              data-index="7"
              className={`icon-card bg-white p-6 rounded-lg cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${visibleMusicCards.includes('7') ? 'visible' : ''}`}
            >
              <h3 className="text-lg font-display font-bold text-gray-800 mb-3" style={{ fontFamily: 'sans-serif', fontWeight: 'bold' }}>TRAVEL GUIDES</h3>
              <p className="text-gray-700 text-sm leading-relaxed text-left" style={{ fontFamily: 'sans-serif' }}>
                Access comprehensive travel guides with cultural context and authentic dialogues by country. Available in all packs, perfect for beginners planning a trip. Learn essential phrases, local tips, and cultural insights to make your journey unforgettable and enriching.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Our Method Section */}
      <section className="py-20" style={{ backgroundColor: '#1e3a8a' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4" style={{ fontFamily: 'sans-serif' }}>
              Why Our Teaching Method
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto" style={{ fontFamily: 'sans-serif' }}>
              Learn Spanish in a Fun, Interactive, and Non-Traditional Way
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* GRADED DIFFICULTY LEVELS */}
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-6">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-white mb-3" style={{ fontFamily: 'sans-serif' }}>GRADED DIFFICULTY LEVELS</h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'sans-serif' }}>
                  Our courses are designed to challenge you gradually. Start from beginner (A1) and progress naturally to advanced (C2) levels through novels, music, films, and interactive content.
                </p>
              </div>
            </div>

            {/* EVERYDAY DIALOGUES */}
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-6">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-white mb-3" style={{ fontFamily: 'sans-serif' }}>IMMERSIVE MULTIMEDIA</h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'sans-serif' }}>
                  Learn through novels, music, films, audio lessons, recipes, and cultural content. Real Spanish as you'll find it in everyday conversations, songs, movies, and authentic cultural experiences.
                </p>
              </div>
            </div>

            {/* MULTIPLE GENRES */}
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-6">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-white mb-3" style={{ fontFamily: 'sans-serif' }}>INTERACTIVE LEARNING</h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'sans-serif' }}>
                  Engage with interactive games for PC and mobile, interactive quizzes, and exercises. Learn vocabulary, grammar, and cultural aspects through fun, non-traditional methods that keep you motivated.
                </p>
              </div>
            </div>

            {/* COMPREHENSIBLE INPUT */}
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-6">
                <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-white mb-3" style={{ fontFamily: 'sans-serif' }}>COMPREHENSIBLE INPUT</h3>
                <p className="text-white/90 leading-relaxed" style={{ fontFamily: 'sans-serif' }}>
                  Learn naturally through context! Whether reading novels, listening to music, watching films, or following recipes, you'll acquire new vocabulary and phrases effortlessly with minimal dictionary use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
              What Our Students Say
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of successful Spanish learners
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
              <p className="text-gray-700 italic mb-6 leading-relaxed">
                "The novels are engaging and the cultural content is fascinating. I've improved my Spanish so much in just a few months!"
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary-300 rounded-full mr-4"></div>
                <div>
                  <p className="font-semibold text-gray-900">Maria S.</p>
                  <p className="text-sm text-gray-600">Intermediate Learner</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
              <p className="text-gray-700 italic mb-6 leading-relaxed">
                "Learning through music has been a game-changer. The lyrics and cultural context make it so much more interesting than traditional methods."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-secondary-300 rounded-full mr-4"></div>
                <div>
                  <p className="font-semibold text-gray-900">John D.</p>
                  <p className="text-sm text-gray-600">Advanced Learner</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
              <p className="text-gray-700 italic mb-6 leading-relaxed">
                "The cultural insights section is amazing! I feel like I'm not just learning a language, but truly understanding the culture."
              </p>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary-400 rounded-full mr-4"></div>
                <div>
                  <p className="font-semibold text-gray-900">Sarah L.</p>
                  <p className="text-sm text-gray-600">Beginner Learner</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Join Our Learning Community
          </h2>
          <p className="text-primary-100 text-xl mb-8">
            Get weekly updates with new content, learning tips, and exclusive offers
          </p>
          <form 
            onSubmit={async (e) => {
              e.preventDefault();
              const email = e.target.querySelector('input[type="email"]').value;
              const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwqahxqztwE0vWHp_9ps2hpW8-lyB_jitEeVV7f2ZfxxGT--T_Ec6d4lynRCLRKDaMm/exec';
              
              try {
                await fetch(GOOGLE_SCRIPT_URL, {
                  method: 'POST',
                  mode: 'no-cors',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    email: email,
                    source: 'Newsletter Home',
                    timestamp: new Date().toISOString()
                  })
                });
                
                alert('¡Gracias! Te has suscrito exitosamente a nuestra comunidad.');
                e.target.reset();
              } catch (error) {
                console.error('Error:', error);
                alert('Hubo un error. Por favor intenta de nuevo.');
              }
            }}
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
          >
            <input
              type="email"
              required
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-300 text-lg"
            />
            <button className="bg-secondary-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary-600 transition transform hover:scale-105">
              Subscribe
            </button>
          </form>
          <p className="text-primary-100 text-sm mt-4">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
      </section>

      {/* Shared Footer */}
      <Footer />
      
      {/* Popup de Notificaciones de Compra */}
      <PurchaseNotification />
    </div>
  );
}

// Main App Component with Router - TODAS LAS PÁGINAS RESTAURADAS
function App() {
  return (
    <CartProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/free-ebook" element={<TrialPage />} />
          <Route path="/article/:id" element={<ArticlePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />

          <Route path="/tutoring-hub" element={<SamplesPage />} />
          <Route path="/download-help" element={<DownloadHelpPage />} />
        </Routes>
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
